import { useCallback, useEffect, useState } from 'react';
import { adminComments, adminDeleteComment } from '@suangua/shared';
import { EmptyRow, LoadingRow, PageHeader, Pagination, StatusBadge, TableCard, useAdminGuard, useFromNow } from '../parts';

/** 评论管理列表项 */
type CommentRow = Awaited<ReturnType<typeof adminComments>>['items'][number];
const PAGE_SIZE = 20;

/**
 * 管理端 · 评论管理：全站评论分页列表
 * 操作：删除（confirm；级联删除其子回复并回减帖子回复数）
 */
export function CommentsPage() {
  const guard = useAdminGuard();
  const fromNow = useFromNow();
  const [items, setItems] = useState<CommentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tip, setTip] = useState('');

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      setError('');
      try {
        const res = await adminComments(p, PAGE_SIZE);
        setItems(res.items);
        setTotal(res.total);
        setPage(res.page);
      } catch (e) {
        setError(guard(e));
      } finally {
        setLoading(false);
      }
    },
    [guard, page],
  );

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function remove(c: CommentRow) {
    if (!window.confirm(`确认删除该评论？（其子回复将一并删除）\n\n${c.content.slice(0, 60)}`)) return;
    setError('');
    setTip('');
    try {
      await adminDeleteComment(c.id);
      setTip(`已删除评论 #${c.id}`);
      await load(page);
    } catch (e) {
      setError(guard(e));
    }
  }

  return (
    <div>
      <PageHeader title="评论管理" desc="全站评论（含楼中楼）按时间倒序，删除为级联操作" />

      {error && <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>}
      {tip && <p className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{tip}</p>}

      <TableCard footer={<Pagination page={page} size={PAGE_SIZE} total={total} onPage={(p) => void load(p)} />}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-100 text-left text-xs text-stone-400">
              <th className="px-4 py-2.5 font-medium">评论内容</th>
              <th className="px-4 py-2.5 font-medium">所属帖子</th>
              <th className="px-4 py-2.5 font-medium">类型</th>
              <th className="px-4 py-2.5 font-medium">时间</th>
              <th className="px-4 py-2.5 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <LoadingRow />
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyRow text="暂无评论" />
                </td>
              </tr>
            ) : (
              items.map((c) => (
                <tr key={c.id} className="border-b border-stone-50 last:border-0 hover:bg-stone-50/60">
                  <td className="max-w-0 px-4 py-2.5">
                    <p className="truncate text-stone-700">{c.content}</p>
                    <p className="text-xs text-stone-400">
                      #{c.id} · {c.username}
                    </p>
                  </td>
                  <td className="max-w-0 px-4 py-2.5">
                    <p className="truncate text-xs text-stone-500">
                      {c.post_title || (c.post_board === 'divination' ? `占卜贴 #${c.post_id}` : `帖子 #${c.post_id}`)}
                    </p>
                  </td>
                  <td className="px-4 py-2.5">
                    {c.parent_id ? <StatusBadge tone="gray">楼中楼</StatusBadge> : <StatusBadge tone="gray">一级</StatusBadge>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-stone-400">{fromNow(c.created_at)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs">
                    <button
                      onClick={() => void remove(c)}
                      className="rounded-md border border-red-200 px-2 py-1 text-red-500 transition-colors hover:bg-red-50"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>
    </div>
  );
}
